import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const ADMIN = "Please contact your platform admin";

const TONES = {
  success: { bg: "#F0FDF4", border: "#BBF7D0", color: "#166534", Icon: CheckCircle2 },
  notice: { bg: "#FFFBEB", border: "#FDE68A", color: "#92400E", Icon: AlertTriangle },
  error: { bg: "#FEF2F2", border: "#FECACA", color: "#991B1B", Icon: XCircle },
};

const outcomeFor = (status, email) => ({
  sent: ["success", <>Reset link sent to <strong>{email}</strong>. It expires in 60 minutes. Check your inbox and spam folder.</>],
  no_account: ["notice", <>There's no CRM account for <strong>{email}</strong>. {ADMIN} to set up your account.</>],
  deactivated: ["notice", <>The CRM account for <strong>{email}</strong> is deactivated. {ADMIN}.</>],
  domain_not_allowed: ["notice", <>Password reset is only available for company email addresses. {ADMIN}.</>],
  rate_limited: ["error", <>Too many attempts. Please wait a few minutes and try again.</>],
  send_failed: ["error", <>We couldn't send the reset email right now. Please try again in a few minutes or contact your platform admin.</>],
}[status] || ["error", <>We couldn't send the reset email right now. Please try again in a few minutes or contact your platform admin.</>]);

function Outcome({ status, email }) {
  const [tone, text] = outcomeFor(status, email);
  const t = TONES[tone];
  return (
    <div data-testid={`forgot-password-outcome-${status}`} role="status"
      style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", borderRadius: 8, background: t.bg, border: `1px solid ${t.border}`, color: t.color, fontSize: 12, lineHeight: 1.55 }}>
      <t.Icon style={{ width: 15, height: 15, flexShrink: 0, marginTop: 2 }} />
      <div data-testid="forgot-password-outcome-text">{text}</div>
    </div>
  );
}

/** Public request-a-reset page. Shows plainly whether a reset link was sent. */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const onSubmit = async (e) => {
    e?.preventDefault();
    setErr(null); setResult(null);
    const value = email.trim();
    if (!value) { setErr("Enter your email to continue."); return; }
    if (!EMAIL_RE.test(value)) { setErr("Enter a valid email address."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/password-reset/request`, { email: value });
      if (data?.status === "invalid_email") setErr("Enter a valid email address.");
      else setResult({ status: data?.status || "send_failed", email: data?.email || value });
    } catch (ex) {
      setResult({ status: ex?.response?.status === 429 ? "rate_limited" : "send_failed", email: value });
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--page-bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} data-testid="forgot-password-page">
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#0F2942" }}>Reset your password</div>
          <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 4 }}>
            Enter your email and we'll send you a reset link.
          </div>
        </div>

        <form onSubmit={onSubmit} className="card" style={{ padding: 24 }} data-testid="forgot-password-form" noValidate>
          <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Email address</label>
          <div style={{ position: "relative", marginTop: 6 }}>
            <Mail style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "#888" }} />
            <input type="email" value={email} autoFocus
              onChange={(e) => { setEmail(e.target.value); setResult(null); setErr(null); }}
              data-testid="forgot-password-email-input"
              placeholder="you@company.com" className="input"
              style={{ paddingLeft: 30, fontSize: 12, width: "100%" }} />
          </div>
          {err && <div data-testid="forgot-password-error" style={{ marginTop: 10, fontSize: 11, color: "#A32D2D" }}>{err}</div>}
          <button type="submit" disabled={loading} className="btn btn-primary" data-testid="forgot-password-submit-btn"
            style={{ marginTop: 18, width: "100%", justifyContent: "center", padding: 10, fontSize: 12, gap: 6 }}>
            {loading ? <><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Sending…</> : "Send reset link"}
          </button>
          {result && <Outcome status={result.status} email={result.email} />}
          <div style={{ marginTop: 14, textAlign: "center" }}>
            <Link to="/login" data-testid="forgot-password-login-link" style={{ fontSize: 11, color: "var(--blue)" }}>
              ← Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
